import React, { useState, useMemo } from 'react';
import {
  Avatar,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import moment from 'moment';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';
import { endLoading, startLoading } from '../../../Redux/loadingSlice';
import Axios from '../../../config/axios';
import { UrlConstant } from '../../../commom/UrlConstant';
import CloseIcon from "@mui/icons-material/Close";
import Repayment from '../Cards/repayment';
import { useNavigate } from 'react-router-dom';
import { renderPaymentIcon } from '../../../utils/utils';
import { CommonDataTable } from '../SharedComponent/CommonDataTable';
import { usePriceFormatter } from '../../../hooks';
import { useAppDispatch } from '../../../Redux/hooks';

interface ActiveLoansProps {
  boosterLoanActiveData: any;
  setIsUpdatePaymentMethod: any;
}

const ActiveLoans: React.FC<ActiveLoansProps> = ({
  boosterLoanActiveData,
  setIsUpdatePaymentMethod
}) => {
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const {
    paymentMethods,
    fetchAllPaymentMethods,
  } = usePaymentMethods();

  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const formatter = usePriceFormatter()

  // Safe date formatting with fallback
  const safeDateFormat = (
    timestamp: any,
    format: string = 'DD-MMM-YYYY'
  ): string => {
    if (!timestamp) return 'N/A';
    try {
      return moment(timestamp).format(format);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const updatePaymentMethod = async (
    loanId: string,
    paymentAccountId: string,
    type: string
  ) => {
    dispatch(startLoading());
    try {
      const response = await Axios.post(
        UrlConstant.BoosterLoan.UpdatePaymentMethod, null,
        {
          params: {
            loanId,
            customerPaymentProfileId: paymentAccountId,
            paymentMethodType: type,
          }
        }
      );

      if (response.data.status) {
        setIsUpdatePaymentMethod(true);
        setOpenPopup(false);
        await fetchAllPaymentMethods();
      }
    } catch (error: any) {
      console.error('Error updating payment method: ', error);
    } finally {
      dispatch(endLoading());
    }
  };

  const navigateToDetail = async (data: any) => {
    navigation('/boosterLoanDetails', {
      state: {
        symbol: data.symbol,
        title: data.symbol,
        loanDetails: data,
        icon: getLogoUrl(data.symbol),
        loan_id: data.id,
      },
    });
  };

  const getLogoUrl = (symbol: string) => {
    if (!symbol) {
      return ''; // Return empty string instead of throwing error
    }
    return `https://logos-api.extend.apexclearing.com/logos/${symbol.toLowerCase().trim()}.png?api_key=4RJeGywzAE&api_secret=isUE1ZzPNi5IzXbs9Qg0S01eaFUcxX09c4U88R9JBiJgJ6WxXHrXz3VtEima`;
  };

  const handleOpenPopup = (row: any) => {
    setSelectedRow(row);
    setOpenPopup(true);
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedRow(null);
  };

  const handleChangeMethod = (method: any) => {
    if (!selectedRow?.id || !method?.id || !method?.type) {
      console.error('Missing required data for payment method change');
      return;
    }
    updatePaymentMethod(selectedRow.id, method.id, method.type);
  };

  // Define columns for CommonDataTable
  const columns = useMemo(() => [
    {
      name: 'Company',
      selector: (row: any) => row.symbol || '',
      cell: (row: any) => (
        <Box display="flex" alignItems="center">
          <Avatar
            src={row.logoUrl ? row.logoUrl : getLogoUrl(row.symbol)}
            alt={row?.symbol || 'Company'}
            style={{ marginRight: '8px', height: '36px', width: '36px' }}
          />
          <Typography style={{ fontSize: '14px', color: 'white' }}>
            {row?.stockName || row?.symbol || 'Unknown Company'}
          </Typography>
        </Box>
      ),
      sortable: true,
      grow: 2,
      minWidth: '200px',
    },
    {
      name: 'Total Instalments',
      selector: (row: any) => row.total_installment || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
          {row.total_installment || 'N/A'}
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Day & Date',
      selector: (row: any) => row.created_at || '',
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
          {safeDateFormat(row.created_at, 'MMM DD, YYYY')} |{' '}
          <span style={{ color: '#4A7AF2' }}>{safeDateFormat(row.created_at, 'dddd')}</span>
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '220px',
    },
    {
      name: 'Amount',
      selector: (row: any) => row.total_amount || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
          {formatter.balance(row.total_amount).display}
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '120px',
    },
    {
      name: 'Payment Method',
      selector: (row: any) => row.payment_method_id || '',
      cell: (row: any) => {
        const method = paymentMethods?.find(
          (m: any) => m.id === row.payment_method_id
        );

        if (!method) {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Typography style={{ color: "white" }}>No method</Typography>
            </Box>
          );
        }

        const icon = renderPaymentIcon(method, 28);
        const details = method.type === 'shareSellDown' || method.type === 'bankAccount'
          ? method.displayName
          : `${method.displayName} ${method.displayNumber}`;

        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {icon}
            <Typography style={{ color: "white", fontSize: '14px' }}>{details}</Typography>
          </Box>
        );
      },
      sortable: false,
      center: true,
      grow: 2,
      minWidth: '220px',
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Button
            style={{
              backgroundColor: '#4A7AF2',
              color: 'white',
              textTransform: 'none',
              borderRadius: '50px',
              fontSize: '14px',
              padding: '5px 10px',
            }}
            startIcon={
              <svg style={{ marginLeft: '5px' }} width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.33325 1.83398H5.99992C2.66659 1.83398 1.33325 3.16732 1.33325 6.50065V10.5007C1.33325 13.834 2.66659 15.1673 5.99992 15.1673H9.99992C13.3333 15.1673 14.6666 13.834 14.6666 10.5007V9.16732" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.6933 2.5135L5.43992 7.76683C5.23992 7.96683 5.03992 8.36017 4.99992 8.64683L4.71325 10.6535C4.60659 11.3802 5.11992 11.8868 5.84659 11.7868L7.85325 11.5002C8.13325 11.4602 8.52659 11.2602 8.73325 11.0602L13.9866 5.80683C14.8933 4.90017 15.3199 3.84684 13.9866 2.5135C12.6533 1.18017 11.5999 1.60684 10.6933 2.5135Z" stroke="white" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.93994 3.26758C10.3866 4.86091 11.6333 6.10758 13.2333 6.56091" stroke="white" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleOpenPopup(row);
            }}
          >
            Change Method
          </Button>
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
      grow: 2,
      minWidth: '200px',
    },
  ], [paymentMethods]);

  return (
    <>
      <div style={{
        marginTop: '30px',
        color: 'white',
        background: 'transparent',
        borderRadius: '20px',
        border: '2px solid #FFFFFF1A',
        padding: '20px',
        width: '100%',
        overflow: 'hidden'
      }}>
        <CommonDataTable
          data={boosterLoanActiveData || []}
          totalRows={boosterLoanActiveData?.length || 0}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          columns={columns}
          title="Active Loans"
          pagination={false}
          rowNavigateTo={(row: any) => ({
            url: '/boosterLoanDetails',
            state: {
              symbol: row.symbol,
              title: row.symbol,
              loanDetails: row,
              icon: getLogoUrl(row.symbol),
              loan_id: row.id,
            }
          })}
          customStyles={{
            table: {
              style: {
                backgroundColor: 'transparent',
                width: '100%',
              },
            },
            tableWrapper: {
              style: {
                backgroundColor: 'transparent',
                width: '100%',
              },
            },
            headCells: {
              style: {
                fontSize: "12px",
                color: "white",
                fontWeight: "600",
                backgroundColor: "transparent",
                borderBottom: "1px solid #FFFFFF1A",
                paddingBottom: "12px",
                paddingTop: "16px",
                paddingLeft: "8px",
                paddingRight: "8px",
              },
            },
            cells: {
              style: {
                color: "white",
                fontSize: '16px',
                border: "none",
                padding: "12px 8px",
                backgroundColor: "transparent",
              },
            },
            rows: {
              style: {
                borderColor: "transparent !important",
                backgroundColor: 'transparent',
                width: '98% !important',
                borderRadius: "8px",
                margin: '2px 8px',
                padding: "4px",
                transition: "all 0.3s ease",
                cursor: 'pointer',
                outline: "none !important",
                '&:hover': {
                  border: "1px solid #4A7AF2 !important",
                  borderColor: "#4A7AF2 !important",
                  backgroundColor: '#4A7AF21A !important',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                  outline: "none !important",
                },
                '&:nth-child(even)': {
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: "1px solid transparent !important",
                  borderColor: "transparent !important",
                },
                '&:nth-child(odd)': {
                  backgroundColor: 'transparent',
                  border: "1px solid transparent !important",
                  borderColor: "transparent !important",
                },
                '&:nth-child(even):hover': {
                  border: "1px solid #4A7AF2 !important",
                  borderColor: "#4A7AF2 !important",
                  backgroundColor: '#4A7AF21A !important',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                },
                '&:nth-child(odd):hover': {
                  border: "1px solid #4A7AF2 !important",
                  borderColor: "#4A7AF2 !important",
                  backgroundColor: '#4A7AF21A !important',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                },
              },
            },
          }}
        />
      </div>

      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className='text-white' sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Change Payment Method
          <IconButton onClick={handleClosePopup} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Repayment
            paymentMethods={paymentMethods}
            setSelectedPaymentMethod={handleChangeMethod}
            fetchPaymentMethods={fetchAllPaymentMethods}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActiveLoans;